import { Box, Button, CircularProgress, MenuItem, Paper, Select, Typography, Alert } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios, { AxiosError } from "axios";
import { Dayjs } from "dayjs";
import * as XLSX from "@e965/xlsx";
import { useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || '/pdfExtractorHttp';

const buildDate = process.env.REACT_APP_BUILD_DATE || 'unknown';

type AggregateByField = 'amount' | 'refId' | 'categoryType';

const AggregateForm: React.FC = () => {

  const [aggregateBy, setAggregateBy] = useState<AggregateByField>('amount');
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    setTotalRecords(0);

    if (aggregateBy == null) {
      return;
    }

    let from, to;
    let payload: any = {
      aggregateBy
    };

    // If no dates are selected, the date object is not sent
    if (fromDate != null || toDate != null) {
      if (fromDate != null) {
        from = fromDate!.valueOf();
      } else {
        from = new Date(0).getTime();
      }

      if (toDate != null) {
        to = toDate!.valueOf();
      } else {
        to = new Date().getTime();
      }

      payload['dateRange'] = {
        from,
        to
      };
    }

    try {

      const response = await axios.post(
        `${API_URL}/aggregate-similarity-data`,
        payload
      );

      if (response.data.message === 'Error' && response.data.error) {
        setError(response.data.error);
      } else if (response.data.message && response.data.message !== 'Success') {
        // Handle any other error messages
        setError(response.data.message);
      } else if (response.data.message === 'Success') {
        setSuccess(response.data.message);

        const headerMap = {
          'amount': 'Transaction Amount',
          'refId': 'Ref ID',
          'categoryType': 'Category Type'
        };
        const headers = [headerMap[aggregateBy], 'Record Count'];
        const rowData = response.data.data;

        let numOfRecords = 0;

        for (let i = 0; i < rowData.length; i++) {
          numOfRecords += rowData[i][1];
        }

        setTotalRecords(numOfRecords);

        // Aggregate Data from API
        const rows = [headers, ...rowData];

        // Create new Worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(rows);

        // Formatting Sheet
        const colWidths = headers.map((header) => ({
          wch: Math.max(header.length, 15),
        }));
        worksheet["!cols"] = colWidths;

        // Create Sheet
        const filename = 'OCR_similarity_record_count_by_' + aggregateBy;

        XLSX.utils.book_append_sheet(workbook, worksheet, "Aggregate Counts");
        const timestamp = new Date().toISOString().split("T")[0];
        XLSX.writeFile(workbook, `${filename}_${timestamp}.xlsx`, { compression: true });
      }
    
    } catch (e) {
      const axiosError = e as AxiosError<{ message: string; error: string }>;
    
      const errorMessage = axiosError.response?.data?.error || 'Failed to process request. Please try again.';
      setError(errorMessage);
        
      console.error('Error:', e);
    } finally {
      setLoading(false);
    }

  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 1200,
          width: '100%'
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          OCR Similarity Database Count Extractor
        </Typography>
        <Typography variant="body1" gutterBottom align="center" color="text.secondary">
          Extract the aggregate count of records used by OCR's similarity check based on the selected field
        </Typography>
        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Results will be returned in an Excel Sheet
          </Typography>
        </Alert>

        <form 
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Box 
            sx={{ 
              display: 'flex',
              rowGap: '15px',
              columnGap: '5%', 
              flexDirection: {
                xs: 'column',
                md: 'row'
              }
            }}
          >
            <Box sx={{ position: 'relative', display: 'flex', flex: 1 }}>
              <Box sx={{ position: 'absolute', left: '0.5rem', top: '-0.5rem', background: 'white', color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.75rem', lineHeight: '1.4375em', letterSpacing: '0.00938em', paddingLeft: '0.25rem', paddingRight: '1rem', py: '0', zIndex: '9' }}>
                Aggregate By *
              </Box>
              <Select
                value={aggregateBy}
                label="Aggregate By *"
                sx={{ flex: 1, minWidth: '0.475' }}
                onChange={(e) => setAggregateBy(e.target.value)}
                margin="none"
                required
                disabled={loading}
              >
                <MenuItem value={'amount'}>Transaction Amount</MenuItem>
                <MenuItem value={'refId'}>Ref ID</MenuItem>
                <MenuItem value={'categoryType'}>Category Type</MenuItem>
              </Select>
            </Box>
            <Box 
              sx={{ 
                position: 'relative', 
                display: 'flex', 
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                flex: 1, 
                minWidth: '0.475', 
                columnGap: '2.5%', 
                alignItems: 'center', 
                px: '0.875rem', 
                py: '1rem', 
                border: '1px solid rgba(0, 0, 0, 0.25)', 
                borderRadius: '0.25rem' 
              }}
            >
              <Box sx={{ position: 'absolute', left: '0.5rem', top: '-0.5rem', background: 'white', color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.75rem', lineHeight: '1.4375em', letterSpacing: '0.00938em', paddingLeft: '0.25rem', paddingRight: '1rem', py: '0' }}>
                Date Range (Optional)
              </Box>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  sx={{ flex: 1, width: '100%' }}
                  onChange={(value) => setFromDate(value)}
                  slotProps={{ field: { clearable: true } }}
                />
                <Box>-</Box>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  sx={{ flex: 1, width: '100%' }}
                  onChange={(value) => setToDate(value)}
                  slotProps={{ field: { clearable: true } }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Processing...
              </Box>
            ) : 'Start Extraction'}
          </Button>
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {error}
            </Typography>
            {error.includes('not found in the system') && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Suggestions:</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  • Verify that you have chosen either Amount or RefId for the Aggregate field
                </Typography>
                <Typography variant="body2" component="div">
                  • Check if you have entered proper Dates for the Date Range (If you are using said field)
                </Typography>
                <Typography variant="body2" component="div">
                  • Check with the Database that holds the data for similarity checks to check whether there are issues with the database
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        {success && !error && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success} - {totalRecords} Records Aggregated
          </Alert>
        )}
      
      </Paper>
      <Box sx={{ mt: 2, textAlign: 'center', color: 'grey.600', fontSize: 12, width: '100%', position: 'fixed', bottom: 8 }}>
        Build date: {buildDate}
      </Box>
    </Box>
  );
}

export default AggregateForm; 